import React from 'react';
import { PencilSimple, ClockCounterClockwise, ClipboardText } from '../../design-system/icons';
import { useCampaignUiStore, useCampaignHeaderQuery, useCampaignCapabilitiesStore, useCampaignEditRequestQuery, useCampaignEditRequestHistoryQuery } from '@discovr/features';
import type { Campaign } from '@discovr/types';
import { CampaignEditForm } from './CampaignEditForm';
import { EditRequestReviewPanel } from './EditRequestReviewPanel';
import { CampaignDetailsCards } from './CampaignDetailsCards';
import { CampaignChangesHistoryView } from './CampaignChangesHistoryView';
import { Button } from '../../components/ui/button';
import { buildDiffMap, buildFieldHistoryMap } from './editRequestDiff';
import { usePublicView } from '../publicView';
import { publicDetailsToCampaign } from './publicDetailsAdapter';

export function CampaignDetailsTab() {
  const { campaignId } = useCampaignUiStore();
  const { isPublicView, campaignDetails } = usePublicView();
  const { data: campaignData } = useCampaignHeaderQuery(campaignId ?? '', { enabled: !isPublicView });
  const { canEditCampaign, reviewEditRequest } = useCampaignCapabilitiesStore();
  const {
    data: editRequest,
    isError: isEditRequestError,
    error: editRequestError,
  } = useCampaignEditRequestQuery(campaignId ?? '', { enabled: !isPublicView });
  const {
    data: editHistory,
    isError: isEditHistoryError,
    error: editHistoryError,
  } = useCampaignEditRequestHistoryQuery(campaignId ?? '', !isPublicView && Boolean(reviewEditRequest));
  const [isEditing, setIsEditing] = React.useState(false);
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);

  // Public mode has no auth, so no self-fetch and no edit/history/review.
  // Adapt the already-fetched public payload into a Campaign-shaped object and
  // render the read-only cards.
  if (isPublicView) {
    if (!campaignDetails) return null;
    const c = publicDetailsToCampaign(campaignDetails);
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-heading-h5 font-semibold text-foreground">Campaign Details</h2>
          <p className="mt-1 text-body-sm-regular text-muted-foreground">
            Everything published and running right now.
          </p>
        </div>
        <CampaignDetailsCards campaign={c} diffMap={new Map()} requestId="" />
      </div>
    );
  }

  const campaign = campaignData;
  if (!campaign) return null;

  if (isEditing) {
    return (
      <CampaignEditForm
        campaign={campaign as Campaign}
        editRequest={editRequest}
        onDone={() => setIsEditing(false)}
      />
    );
  }

  // Brand-only "Changes history" — the admin overlay below already shows the
  // live open request inline at all times, so it has no separate toggle.
  if (historyOpen && !reviewEditRequest) {
    return (
      <CampaignChangesHistoryView
        campaign={campaign as Campaign}
        campaignId={campaignId ?? ''}
        onClose={() => setHistoryOpen(false)}
      />
    );
  }

  const c = campaign as Campaign;
  const locked = c.brief_locked;
  // Inline before→after diff only makes sense for the reviewer — the brand's
  // own pending changes surface via the "awaiting a decision" banner instead
  // (and via "Changes history" for anything already resolved).
  const diffMap = reviewEditRequest ? buildDiffMap(editRequest) : new Map();
  const fieldHistory = reviewEditRequest && editRequest ? buildFieldHistoryMap(editHistory ?? [], editRequest.id) : new Map();
  const requestId = editRequest?.id ?? '';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-heading-h5 font-semibold text-foreground">Campaign Details</h2>
          <p className="mt-1 text-body-sm-regular text-muted-foreground">
            Everything published and running right now.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!reviewEditRequest && (
            <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)} className="gap-1.5">
              <ClockCounterClockwise size={14} />
              Changes history
            </Button>
          )}
          {reviewEditRequest && editRequest && (
            <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)} className="gap-1.5">
              <ClipboardText size={14} />
              Review changes
            </Button>
          )}
          {canEditCampaign && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              disabled={locked}
              title={locked ? "Changes are locked once a campaign goes live." : undefined}
              className="gap-1.5"
            >
              <PencilSimple size={14} />
              Edit Campaign
            </Button>
          )}
        </div>
      </div>

      {(isEditRequestError || isEditHistoryError) && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-body-sm-regular text-rose-800">
          Couldn&apos;t load edit history —{' '}
          {(editRequestError instanceof Error && editRequestError.message) ||
            (editHistoryError instanceof Error && editHistoryError.message) ||
            'please refresh to try again.'}{' '}
          Any pending changes shown below may be incomplete.
        </div>
      )}

      {locked && (
        <div className="rounded-lg border border-border bg-muted px-4 py-3 text-body-sm-regular text-muted-foreground">
          Can&apos;t edit a live campaign — changes are locked once a campaign goes live.
        </div>
      )}

      {editRequest && reviewEditRequest && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-body-sm-regular text-amber-800">
          Reviewing campaign changes — {editRequest.changes.length} field{editRequest.changes.length === 1 ? '' : 's'} changed.
        </div>
      )}
      {editRequest && !reviewEditRequest && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-body-sm-regular text-amber-800">
          Changes are pending review — {editRequest.changes.filter((c) => c.status === 'pending').length} field
          {editRequest.changes.filter((c) => c.status === 'pending').length === 1 ? '' : 's'} awaiting a decision.
        </div>
      )}
      {editRequest && reviewEditRequest && (
        <EditRequestReviewPanel open={reviewOpen} onOpenChange={setReviewOpen} editRequest={editRequest} editHistory={editHistory ?? []} currency={c.budget_currency} />
      )}

      <CampaignDetailsCards campaign={c} diffMap={diffMap} requestId={requestId} fieldHistory={fieldHistory} />
    </div>
  );
}
